// @flow strict
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {
  getInterviewSchedules,
  getCandidateFeedbacks,
} from 'src/action-creators/crm';
import {useInflight} from 'src/hooks/redux';
import useScrollLoader from 'src/hooks/useScrollLoader';
import {useSelector, useDispatch} from 'react-redux';
import {
  selectCompletedCandidateFeedbacks,
  selectCompletedInterviewSchedules,
} from 'src/selectors/crm';
import {
  SimpleButtonDropdown,
  EmptyState,
} from '@spaced-out/ui-design-system/lib/components';
import {CompletedCard} from 'src/components/crm/jobs/job-info/overview/feedbacks/cards/completed-card.jsx';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import css from './completed-feedback-tab.css';


type FeedbackType = 'interview' | 'applicant';

type CompletedFeedbackTabProps = {
  jobId?: string,
  from?: string,
};

export const CompletedFeedbackTab = ({
  jobId,
  from,
}: CompletedFeedbackTabProps): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const [feedbackType, setFeedbackType] =
    React.useState<FeedbackType>('interview');
  const [pagination, setPagination] = React.useState({
    interview: {page: 0, hasMore: true},
    applicant: {page: 0, hasMore: true},
  });
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const [feedbacks, setFeedbacks] = React.useState({
    interview: [],
    applicant: [],
  });

  const candidateFeedbacks = useSelector(selectCompletedCandidateFeedbacks);
  const interviewFeedbacks = useSelector(selectCompletedInterviewSchedules);

  const isCandidateFeedbacksLoading = useInflight(
    getCandidateFeedbacks('COMPLETED', jobId),
  );
  const isInterviewFeedbacksLoading = useInflight(
    getInterviewSchedules('COMPLETED', jobId),
  );

  const isInitialLoading = useAsyncDependency(
    () =>
      Promise.all([
        dispatch(getCandidateFeedbacks('COMPLETED', jobId)),
        dispatch(getInterviewSchedules('COMPLETED', jobId)),
      ]),
    [],
  );

  // Process and deduplicate feedbacks
  const processFeedbacks = (prevFeedbacks, newContent) => {
    if (!newContent) {
      return prevFeedbacks;
    }

    const combined = [...prevFeedbacks, ...newContent];
    return combined.reduce((acc, item) => {
      if (
        item?.interviewReviews &&
        !acc.find((existing) => existing.id === item.id)
      ) {
        acc.push(item);
      }
      return acc;
    }, []);
  };

  // Update feedbacks when data loads
  React.useEffect(() => {
    if (isInitialLoading) {
      return;
    }

    if (interviewFeedbacks?.content) {
      setFeedbacks((prev) => ({
        ...prev,
        interview: processFeedbacks(prev.interview, interviewFeedbacks.content),
      }));

      if (interviewFeedbacks.last) {
        setPagination((prev) => ({
          ...prev,
          interview: {...prev.interview, hasMore: false},
        }));
      }
    }

    if (candidateFeedbacks?.content) {
      setFeedbacks((prev) => ({
        ...prev,
        applicant: processFeedbacks(prev.applicant, candidateFeedbacks.content),
      }));

      if (candidateFeedbacks.last) {
        setPagination((prev) => ({
          ...prev,
          applicant: {...prev.applicant, hasMore: false},
        }));
      }
    }
  }, [isInitialLoading, interviewFeedbacks, candidateFeedbacks]);

  const loadMoreFeedbacks = React.useCallback(() => {
    if (isFetchingMore || !pagination[feedbackType].hasMore) {
      return;
    }

    setIsFetchingMore(true);
    const nextPage = pagination[feedbackType].page + 1;

    const fetchAction =
      feedbackType === 'interview'
        ? getInterviewSchedules('COMPLETED', jobId, nextPage)
        : getCandidateFeedbacks('COMPLETED', jobId, nextPage);

    dispatch(fetchAction)
      .then((result) => result.payload || result)
      .then((response) => {
        setPagination((prev) => ({
          ...prev,
          //$FlowFixMe[invalid-computed-prop]
          [feedbackType]: {
            page: nextPage,
            hasMore: !response?.last,
          },
        }));

        if (response?.content) {
          setFeedbacks((prev) => ({
            ...prev,
            //$FlowFixMe[invalid-computed-prop]
            [feedbackType]: processFeedbacks(
              prev[feedbackType],
              response.content,
            ),
          }));
        }

        setIsFetchingMore(false);
      })
      .catch((error) => {
        console.error('Error loading more feedbacks:', error);
        setIsFetchingMore(false);
      });
  }, [dispatch, jobId, feedbackType, isFetchingMore, pagination]);

  const [containerRef, loaderRef, handleScroll] = useScrollLoader({
    loading: isFetchingMore,
    done: !pagination[feedbackType].hasMore,
    onLoadMore: loadMoreFeedbacks,
  });

  const refreshFeedbacks = () => {
    dispatch(getCandidateFeedbacks('COMPLETED', jobId));
    dispatch(getInterviewSchedules('COMPLETED', jobId));
  };

  const handleFeedbackTypeChange = (option) => {
    //$FlowFixMe[incompatible-call]
    setFeedbackType(option.key);
  };

  const renderFeedbackItems = () => {
    const currentFeedbacks = feedbacks[feedbackType].filter(
      (item) => item?.interviewReviews,
    );

    if (currentFeedbacks.length === 0) {
      return (
        <EmptyState
          title={t('No_Feedbacks_Yet', 'No Feedbacks Yet')}
          description={`${t(
            'All_clear_for_now_Keep_an_eye_out',
            'All clear for now. Keep an eye out',
          )}!`}
          imageVariant="file"
        />
      );
    }

    return (
      <div
        className={css.feedbackList}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {currentFeedbacks.map((feedback) =>
          feedback.interviewReviews.map((review, index) => (
            <CompletedCard
              key={`${feedback.id}-${index}`}
              completedData={feedback}
              review={review}
              type={feedbackType}
              isSmall={true}
              onUpdateFeedbacks={refreshFeedbacks}
              from={from}
            />
          )),
        )}

        {pagination[feedbackType].hasMore && (
          <div ref={loaderRef} className={css.loadingMoreContainer}>
            {isFetchingMore && <CircularLoader size="small" />}
          </div>
        )}
      </div>
    );
  };

  const feedbackTypeOptions = [
    {key: 'interview', label: t('Interview_Feedback', 'Interview Feedback')},
    {key: 'applicant', label: t('Applicant_Review', 'Applicant Review')},
  ];

  const isLoading =
    isInitialLoading ||
    (isCandidateFeedbacksLoading && !isFetchingMore) ||
    (isInterviewFeedbacksLoading && !isFetchingMore);

  return (
    <div className={css.completedTab}>
      {isLoading ? (
        <CircularLoader className={css.loader} size="large" />
      ) : (
        <>
          <div>
            <SimpleButtonDropdown
              size="small"
              type="ghost"
              children={
                feedbackTypeOptions.find(
                  (option) => option.key === feedbackType,
                )?.label
              }
              anchorPosition="bottom-start"
              onOptionSelect={handleFeedbackTypeChange}
              options={feedbackTypeOptions}
              selectedKeys={[feedbackType]}
              selectedOption={feedbackType}
            />
          </div>
          {renderFeedbackItems()}
        </>
      )}
    </div>
  );
};
